// src/pages/SuperAdminUsers_Add.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import {
  UserPlus,
  Users,
  Mail,
  Shield,
  KeyRound,
  ArrowLeft,
  Eye,
  EyeOff,
  Sparkles,
} from "lucide-react";

const API = "https://superadmin.nissiindia.com/"; // adjust if needed
const ROLES = ["SUPERADMIN", "AUDITOR"];

export default function SuperAdminUsers_Add() {
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    username: "",
    display_name: "",
    email: "",
    role: "SUPERADMIN",
    is_active: 1,
    password: "",
    confirm: "",
  });

  // NEW: reveal toggles for password fields
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    if (!form.username.trim()) return "Username is required";
    if (!/^[A-Za-z0-9_.-]{3,100}$/.test(form.username.trim()))
      return "Username must be 3-100 chars (A-Z, a-z, 0-9, _ . -)";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) return "Invalid email";
    if (!form.password) return "Password is required";
    if (form.password.length < 8) return "Password must be at least 8 characters";
    if (form.password !== form.confirm) return "Passwords do not match";
    if (!ROLES.includes(form.role)) return "Invalid role";
    return "";
  };

  const submit = async () => {
    const err = validate();
    if (err) return Swal.fire("Validation", err, "warning");

    try {
      setSaving(true);
      Swal.fire({
        title: "Creating user...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const payload = {
        username: form.username.trim(),
        display_name: form.display_name.trim() || null,
        email: form.email.trim() || null,
        role: form.role,
        is_active: Number(form.is_active) ? 1 : 0,
        password: form.password, // backend will hash via argon2id
      };
      await axios.post(`${API}/api/superadmin/users`, payload);
      Swal.close();
      Swal.fire({ icon: "success", title: "User created" });
      nav("/superAdmin/superAdminUsers_List");
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.error || e.response?.data?.debug || "Create failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(1200px_600px_at_50%_-100px,rgba(59,130,246,0.15),transparent)] bg-slate-100">
      {/* Responsive layout: drawer sidebar on mobile; 25%/75% on md+ */}
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        {/* Main content */}
        <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
          {/* Header: pill bar with subtle glass effect */}
          <div className="px-4 sm:px-6 lg:px-8 pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">Add Super Admin</h1>
                  <p className="text-sm text-slate-600">Create a new global admin account</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => nav(-1)}
                  className="inline-flex items-center gap-2 rounded-xl bg-white/70 px-3 py-1.5 text-slate-800 ring-1 ring-slate-200 backdrop-blur hover:bg-white"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <div className="rounded-xl bg-white/70 px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-200 backdrop-blur">
                  <Time />
                </div>
              </div>
            </div>

            {/* Chips row */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs text-slate-700 ring-1 ring-slate-200">
                <Shield className="h-4 w-4 text-indigo-600" /> Role & status
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs text-slate-700 ring-1 ring-slate-200">
                <KeyRound className="h-4 w-4 text-blue-600" /> Strong password
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs text-slate-700 ring-1 ring-slate-200">
                <Sparkles className="h-4 w-4 text-cyan-600" /> Glass UI
              </span>
            </div>
          </div>

          {/* Form card (glass-like) */}
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200/70 bg-white/80 p-5 shadow-xl backdrop-blur">
              {/* Section header */}
              <div className="flex items-center gap-2 border-b border-slate-200/70 bg-gradient-to-r from-slate-50 to-white/60 px-4 py-3 rounded-xl">
                <UserPlus className="h-4 w-4 text-slate-700" />
                <h3 className="text-sm font-semibold text-slate-800">User Details</h3>
              </div>

              {/* Fields grid */}
              <div className="p-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Username */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Username *
                    </label>
                    <input
                      value={form.username}
                      onChange={onChange("username")}
                      placeholder="e.g. root"
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-300"
                    />
                  </div>

                  {/* Display name */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Display Name
                    </label>
                    <input
                      value={form.display_name}
                      onChange={onChange("display_name")}
                      placeholder="e.g. Main Super Admin"
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-300"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        placeholder="admin@example.com"
                        className="w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-300"
                      />
                    </div>
                  </div>

                  {/* Role */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Role
                    </label>
                    <div className="relative">
                      <Shield className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <select
                        value={form.role}
                        onChange={onChange("role")}
                        className="w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 shadow-sm focus:outline-none"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Status
                    </label>
                    <select
                      value={form.is_active}
                      onChange={onChange("is_active")}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:outline-none"
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Password *
                    </label>
                    <div className="relative">
                      <KeyRound className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type={showPwd ? "text" : "password"}
                        value={form.password}
                        onChange={onChange("password")}
                        className="w-full rounded-xl border border-slate-300 bg-white pl-9 pr-10 py-2 text-slate-900 shadow-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-300"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((s) => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:bg-slate-100"
                        title={showPwd ? "Hide password" : "Show password"}
                      >
                        {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">At least 8 characters.</p>
                  </div>

                  {/* Confirm */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Confirm *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirm ? "text" : "password"}
                        value={form.confirm}
                        onChange={onChange("confirm")}
                        className="w-full rounded-xl border border-slate-300 bg-white pr-10 px-3 py-2 text-slate-900 shadow-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-300"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((s) => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:bg-slate-100"
                        title={showConfirm ? "Hide text" : "Show text"}
                      >
                        {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-col sm:flex-row items-center gap-2 sm:justify-between">
                  <p className="text-xs text-slate-600">
                    The backend will securely hash the password (argon2id).
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={submit}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-black focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
                    >
                      <UserPlus className="h-4 w-4" />
                      {saving ? "Saving..." : "Save"}
                    </button>

                    {/* Keep your original cancel route exactly as-is */}
                    <Link
                      to="/superadmin/users"
                      className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-slate-900 ring-1 ring-slate-300 shadow hover:bg-slate-50"
                    >
                      Cancel
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Spacer */}
            <div className="h-6" />
          </div>
        </main>
      </div>
    </div>
  );
}
